/**
 * The documents themselves, as cards or as rows.
 *
 * Both draw from the same `DocumentSummary` and offer the same actions; the
 * difference is only where things sit. A card is one stretched link (the title) with
 * the star and the menu on top of it, so the whole card opens the document, a
 * middle-click opens it in a tab, and the two buttons are real buttons and not
 * links inside a link.
 *
 * Pointing at a card starts the editor's chunk loading, and pressing it starts the
 * document's metadata (core/prefetch.ts): by the time the click lands there is
 * little left to wait for. Opening is a View Transition where the browser has them:
 * the card's thumbnail grows into the page.
 */

import { flushSync } from 'react-dom';
import type React from 'react';
import type { DocumentSummary } from '@shared/api';
import { Icon, dotsThreeIcon, starFillIcon, starIcon, lockSimpleIcon, usersIcon } from '../core/icons';
import { editorIsLoaded, prefetchDocument, prefetchEditor } from '../core/prefetch';
import { Menu, type MenuItem } from '../core/ui';
import { viewTransition } from '../motion/viewTransition';
import { useRouter } from '../router';
import { formatWhen } from '../theme';
import { arrowCounterClockwiseIcon, arrowSquareOutIcon, copyIcon, pencilSimpleIcon, trashIcon } from '../core/icons';
import { dateColumn, docPath, formatBytes, ownerLabel, type HomeView } from './home';
import { DocumentThumb } from './DocumentThumb';
import { KIND_GLYPH } from './Thumb';

export interface RowActions {
  onStar: (doc: DocumentSummary) => void;
  onRename: (doc: DocumentSummary) => void;
  onCopy: (doc: DocumentSummary) => void;
  onTrash: (doc: DocumentSummary) => void;
  onRestore: (doc: DocumentSummary) => void;
  onDelete: (doc: DocumentSummary) => void;
}

/** What the ⋯ menu offers for this document in this view. */
export function menuFor(doc: DocumentSummary, view: HomeView, a: RowActions, open: () => void): MenuItem[] {
  const owner = doc.role === 'owner';
  if (view === 'trash') {
    return [
      { label: 'Restore', icon: arrowCounterClockwiseIcon, onSelect: () => a.onRestore(doc), disabled: !owner },
      { label: 'Delete forever', icon: trashIcon, onSelect: () => a.onDelete(doc), danger: true, disabled: !owner, separated: true }
    ];
  }
  const items: MenuItem[] = [{ label: 'Open', icon: arrowSquareOutIcon, onSelect: open }];
  if (owner) items.push({ label: 'Rename', icon: pencilSimpleIcon, onSelect: () => a.onRename(doc) });
  items.push({ label: doc.starred ? 'Remove star' : 'Add star', icon: doc.starred ? starFillIcon : starIcon, onSelect: () => a.onStar(doc) });
  // A locked document is one body of ciphertext the server cannot copy.
  if (!doc.locked) items.push({ label: 'Make a copy', icon: copyIcon, onSelect: () => a.onCopy(doc) });
  if (owner) items.push({ label: 'Move to trash', icon: trashIcon, onSelect: () => a.onTrash(doc), danger: true, separated: true });
  return items;
}

/** Open a document the way a click should: prefetched, and morphing where it can. */
function useOpen() {
  const { navigate } = useRouter();
  return (doc: DocumentSummary, from: HTMLElement | null) => {
    prefetchEditor(doc.kind);
    prefetchDocument(doc.id);
    void viewTransition(() => flushSync(() => navigate(docPath(doc.kind, doc.id))), {
      from,
      to: () => document.querySelector<HTMLElement>('[data-doc-page]'),
      // The skeleton's paper is swapped for the real page a few milliseconds on, and a morph
      // whose landing element goes is over. With the editor's code here, the real page is a
      // moment away: land on that (a Docs paper, or Sheets' grid). Without it, land on the
      // skeleton at once.
      stays: (el) => el.classList.contains('mv-paper') || el.classList.contains('mv-sheet-page'),
      settleMs: editorIsLoaded(doc.kind) ? 150 : 0
    });
  };
}

const unmodified = (e: React.MouseEvent): boolean => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0);

/** The props that make a link to a document start its own loading. */
function intent(doc: DocumentSummary) {
  return {
    onPointerEnter: () => prefetchEditor(doc.kind),
    onFocus: () => prefetchEditor(doc.kind),
    // A press is a decision; a hover is not (the metadata fetch records an open).
    onPointerDown: () => {
      prefetchEditor(doc.kind);
      prefetchDocument(doc.id);
    }
  };
}

function StarButton({ doc, onStar }: { doc: DocumentSummary; onStar: (d: DocumentSummary) => void }) {
  return (
    <button
      type="button"
      className="mv-card-star"
      aria-label={doc.starred ? `Remove star from ${doc.title}` : `Star ${doc.title}`}
      aria-pressed={doc.starred}
      data-on={doc.starred ? 'true' : undefined}
      onClick={() => onStar(doc)}
    >
      <Icon icon={doc.starred ? starFillIcon : starIcon} size={16} />
    </button>
  );
}

function Marks({ doc }: { doc: DocumentSummary }) {
  return (
    <>
      {doc.shared && <Icon icon={usersIcon} size={15} className="mv-mark" label="Shared" />}
      {doc.locked && <Icon icon={lockSimpleIcon} size={15} className="mv-mark" label="Locked" />}
    </>
  );
}

/** The small coloured square with the kind's glyph: the app's colour, solid. */
function KindBadge({ doc, size }: { doc: DocumentSummary; size: number }) {
  return (
    <span className="mv-kind" style={{ width: size, height: size }}>
      <Icon icon={KIND_GLYPH[doc.kind]} size={Math.round(size * 0.65)} />
    </span>
  );
}

function meta(doc: DocumentSummary, view: HomeView, meId: string | null): string {
  const when = dateColumn(doc, view);
  const owner = doc.ownerId === meId ? null : doc.ownerName;
  return `${owner ? `${owner} · ` : ''}${when.label} ${formatWhen(when.at)}`;
}

export function DocumentCard({
  doc,
  view,
  meId,
  actions,
  leaving,
  item
}: {
  doc: DocumentSummary;
  view: HomeView;
  meId: string | null;
  actions: RowActions;
  leaving: boolean;
  item: (key: string) => (el: HTMLElement | null) => void;
}) {
  const open = useOpen();
  const trashed = view === 'trash';
  const link = (e: React.MouseEvent) => {
    if (!unmodified(e)) return;
    e.preventDefault();
    open(doc, (e.currentTarget as HTMLElement).closest('.mv-rcard')?.querySelector<HTMLElement>('.mv-rcard-thumb') ?? null);
  };

  return (
    <article ref={item(doc.id)} className="mv-rcard mv-lift" data-app={doc.kind} data-leaving={leaving ? 'true' : undefined}>
      <div className="mv-rcard-thumb">
        <DocumentThumb doc={doc} />
      </div>
      <div className="mv-rcard-body">
        {trashed ? (
          <span className="mv-rcard-title mv-ellipsis">{doc.title}</span>
        ) : (
          <a className="mv-rcard-title mv-rcard-link mv-ellipsis" href={docPath(doc.kind, doc.id)} onClick={link} {...intent(doc)}>
            {doc.title}
          </a>
        )}
        <span className="mv-rcard-meta">
          <KindBadge doc={doc} size={20} />
          <span className="mv-ellipsis" style={{ flex: 1 }}>
            {meta(doc, view, meId)}
          </span>
          <Marks doc={doc} />
        </span>
      </div>
      <span className="mv-rcard-actions" data-pinned={doc.starred ? 'true' : undefined}>
        {!trashed && <StarButton doc={doc} onStar={actions.onStar} />}
        <Menu
          label={`Actions for ${doc.title}`}
          icon={dotsThreeIcon}
          items={menuFor(doc, view, actions, () => open(doc, null))}
          trigger={({ open: isOpen, toggle, ref }) => (
            <button
              ref={ref}
              type="button"
              className="mv-card-star"
              aria-label={`Actions for ${doc.title}`}
              aria-haspopup="menu"
              aria-expanded={isOpen}
              onClick={toggle}
            >
              <Icon icon={dotsThreeIcon} size={16} />
            </button>
          )}
        />
      </span>
    </article>
  );
}

export function DocumentRow({
  doc,
  view,
  meId,
  actions,
  leaving,
  item
}: {
  doc: DocumentSummary;
  view: HomeView;
  meId: string | null;
  actions: RowActions;
  leaving: boolean;
  item: (key: string) => (el: HTMLElement | null) => void;
}) {
  const open = useOpen();
  const trashed = view === 'trash';
  const when = dateColumn(doc, view);
  const link = (e: React.MouseEvent) => {
    if (!unmodified(e)) return;
    e.preventDefault();
    open(doc, null);
  };

  return (
    <div ref={item(doc.id)} role="row" className="mv-row mv-lrow" data-app={doc.kind} data-leaving={leaving ? 'true' : undefined}>
      <span className="mv-lrow-tile">
        <Icon icon={KIND_GLYPH[doc.kind]} size={18} />
      </span>
      <span className="mv-lrow-title">
        {trashed ? (
          <span className="mv-ellipsis" style={{ fontWeight: 600, color: 'var(--fg-1)' }}>
            {doc.title}
          </span>
        ) : (
          <a className="mv-rcard-link mv-ellipsis" style={{ fontWeight: 600, color: 'var(--fg-1)' }} href={docPath(doc.kind, doc.id)} onClick={link} {...intent(doc)}>
            {doc.title}
          </a>
        )}
        <Marks doc={doc} />
      </span>
      <span className="mv-col-owner mv-ellipsis mv-lrow-cell" style={{ width: 150 }}>
        {ownerLabel(doc, meId)}
      </span>
      <span className="mv-col-date mv-lrow-cell" style={{ width: 120 }} title={when.label}>
        {formatWhen(when.at)}
      </span>
      <span className="mv-col-size mv-lrow-cell mv-lrow-mono" style={{ width: 64, textAlign: 'right' }}>
        {formatBytes(doc.sizeBytes)}
      </span>
      <span className="mv-lrow-actions">
        {!trashed && <StarButton doc={doc} onStar={actions.onStar} />}
        <Menu label={`Actions for ${doc.title}`} icon={dotsThreeIcon} items={menuFor(doc, view, actions, () => open(doc, null))} />
      </span>
    </div>
  );
}
